import {
  Menu,
  Search,
  Bell,
  Moon,
  Sun,
  User,
  Settings,
  LogOut,
  X,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

export function Header({ onMenuClick }) {
  const [theme, setTheme] = useState("light");
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const {logout} = useAuth();

  const notifications = [
    {
      id: 1,
      title: "New order received",
      message: "John Doe placed an order for Tech Conference 2026",
      time: "2m ago",
    },
    {
      id: 2,
      title: "New event created",
      message: "Music Festival 2026 has been published",
      time: "1h ago",
    },
    {
      id: 3,
      title: "New review posted",
      message: "Sarah gave 5 stars to your event",
      time: "3h ago",
    },
  ];

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.documentElement.classList.toggle("dark");
  };

  const handleLogout = () => {
    console.log("Logging out...");
    logout();
    navigate("/");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-30 flex md:h-20 h-14 items-center gap-4 border-b border-[#F4A696]/20 bg-white px-4 backdrop-blur-sm sm:px-6 lg:px-8">
      {/* Mobile Menu Button */}
      <button
        onClick={onMenuClick}
        className="rounded-xl p-2 text-[#1B3C5C]/70 hover:bg-[#FFF4F0] hover:text-[#F4A696] transition-colors lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Search Bar - Desktop */}
      <div className="flex flex-1 items-center gap-4">
        <div className="relative hidden w-full max-w-md md:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#F4A696]" />
          <input
            type="search"
            placeholder="Search events, orders, users..."
            className="w-full rounded-xl border border-[#F4A696]/30 bg-[#FFF4F0]/50 py-2.5 pl-10 pr-4 text-sm text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
          />
        </div>
      </div>

      {/* Right Side Actions */}
      <div className="flex items-center gap-2">
        {/* Mobile Search Button */}
        <button
          onClick={() => setSearchOpen(!searchOpen)}
          className="rounded-xl p-2 text-[#1B3C5C]/70 hover:bg-[#FFF4F0] hover:text-[#F4A696] transition-colors md:hidden"
        >
          <Search className="h-5 w-5" />
        </button>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="rounded-xl p-2 text-[#1B3C5C]/70 hover:bg-[#FFF4F0] hover:text-[#F4A696] transition-colors"
        >
          {theme === "light" ? (
            <Moon className="h-5 w-5" />
          ) : (
            <Sun className="h-5 w-5" />
          )}
        </button>

        {/* Notifications Dropdown */}
        <div className="relative" ref={notificationRef}>
          <button
            onClick={() => setNotificationOpen(!notificationOpen)}
            className="relative rounded-xl p-2 text-[#1B3C5C]/70 hover:bg-[#FFF4F0] hover:text-[#F4A696] transition-colors"
          >
            <Bell className="h-5 w-5" />
            <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-[#F4A696] to-[#E89580] text-xs font-bold text-white shadow-lg">
              3
            </span>
          </button>

          {/* Notifications Dropdown Menu */}
          {notificationOpen && (
            <div className="absolute right-0 mt-2 w-80 rounded-2xl border border-[#F4A696]/20 bg-white shadow-xl">
              <div className="border-b border-[#F4A696]/20 bg-gradient-to-r from-[#FFF4F0] to-white px-4 py-3">
                <h3 className="font-semibold text-[#1B3C5C]">
                  Notifications
                </h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="border-b border-[#F4A696]/10 px-4 py-3 hover:bg-[#FFF4F0]/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <p className="text-sm font-medium text-[#1B3C5C]">
                        {notification.title}
                      </p>
                      <span className="text-xs text-[#F4A696]">
                        {notification.time}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#1B3C5C]/60">
                      {notification.message}
                    </p>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#F4A696]/20 px-4 py-3 text-center">
                <button className="text-sm font-medium text-[#F4A696] hover:text-[#E89580] transition-colors">
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User Menu Dropdown */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#F4A696] to-[#E89580] text-white shadow-lg hover:shadow-xl transition-shadow"
          >
            <span className="text-sm font-semibold">AD</span>
          </button>

          {/* User Dropdown Menu */}
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-2xl border border-[#F4A696]/20 bg-white shadow-xl">
              <div className="border-b border-[#F4A696]/20 bg-gradient-to-r from-[#FFF4F0] to-white px-4 py-3">
                <p className="text-sm font-medium text-[#1B3C5C]">
                  Admin User
                </p>
                <p className="text-xs text-[#F4A696]">admin@eventhouse.com</p>
              </div>
              <div className="py-2">
                <button
                  onClick={() => {
                    navigate("/admin/profile");
                    setUserMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[#1B3C5C]/70 hover:bg-[#FFF4F0] hover:text-[#F4A696] transition-colors"
                >
                  <User className="h-4 w-4" />
                  Profile
                </button>
                <button
                  onClick={() => {
                    navigate("/admin/settings");
                    setUserMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[#1B3C5C]/70 hover:bg-[#FFF4F0] hover:text-[#F4A696] transition-colors"
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </button>
              </div>
              <div className="border-t border-[#F4A696]/20 py-2">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-white md:hidden">
          <div className="flex h-20 items-center gap-4 border-b border-[#F4A696]/20 px-4">
            <button
              onClick={() => setSearchOpen(false)}
              className="rounded-xl p-2 text-[#1B3C5C]/70 hover:bg-[#FFF4F0] hover:text-[#F4A696]"
            >
              <X className="h-5 w-5" />
            </button>
            <input
              type="search"
              placeholder="Search..."
              autoFocus
              className="flex-1 border-none bg-transparent text-sm text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:outline-none"
            />
          </div>
        </div>
      )}
    </header>
  );
}
